import classNames from "classnames";
import { ItemSelectionKind, useIsTouchDevice } from "client/Hooks";
import { setTrackingId } from "client/Misc/utils";
import {
	MouseEvent,
	PropsWithChildren,
	ReactElement,
	useCallback,
	useContext,
	useEffect,
	useState
} from "react";
import { CenteredCheckbox } from "../Checkbox/CenteredCheckbox";
import {
	ColumnContextData,
	ColumnsContext,
	RowCellIndexContext,
	RowContext
} from "./contexts";
import styles from "./TableView.module.less";

export type TableViewCellProps = {
	shouldExecuteOnClick?: boolean;
	tooltip?: string;
};

export const Cell = (props: PropsWithChildren<TableViewCellProps>): ReactElement => {
	const {
		shouldExecuteOnClick = false,
		tooltip,
		children
	} = props;

	const {
		onExecute
	} = useContext(RowContext);

	const cellIndex: number = useContext(RowCellIndexContext);

	const {
		columns
	} = useContext(ColumnsContext);

	const column: ColumnContextData | undefined = columns[cellIndex];
	const columnTrackingId: string | undefined = column?.trackingId;

	const [shouldRenderContent, setShouldRenderContent] = useState<boolean>(!column?.isExpensiveToRender);

	useEffect(
		(): EffectWithDestructorResult => {
			if (!column?.isExpensiveToRender) {
				return undefined;
			}

			const handle: SetTimeoutHandle = setTimeout(
				() => setShouldRenderContent(true),
				25 + Math.random() * 25
			);

			return () => clearTimeout(handle);
		},
		[column?.isExpensiveToRender]
	);

	const clickHandler = useCallback(
		(): void => {
			if (shouldExecuteOnClick && onExecute) {
				onExecute();
			}
		},
		[
			onExecute,
			shouldExecuteOnClick
		]
	);

	const doubleClickHandler = useCallback(
		(event: MouseEvent): void => {
			if (!shouldExecuteOnClick && onExecute) {
				event.preventDefault();
				onExecute();
			}
		},
		[onExecute, shouldExecuteOnClick]
	);

	return (
		<InternalCell
			onClick={clickHandler}
			onDoubleClick={doubleClickHandler}
			columnTrackingId={columnTrackingId}
			isClickable={shouldExecuteOnClick}
			maxWidthOfAutoColumn={column?.maxWidthOfAutoColumn}
		>
			<div
				className={styles.cellContent}
				title={tooltip}
			>
				{shouldRenderContent && children}
			</div>
		</InternalCell>
	);
};
Cell.displayName = "TableView.Cell";

export const SelectCell = (): ReactElement => {
	const {
		selectable,
		shouldHideDisabledCheckboxes,
		hasHover,
		isDisabled
	} = useContext(RowContext);

	const isTouch: boolean = useIsTouchDevice();

	const shouldRenderNormalCheckbox: boolean = hasHover
		|| isDisabled
		|| selectable && selectable.isSelected
		|| isTouch;

	return (
		<InternalCell
			columnTrackingId="selection-column"
			isClickable={false}
			maxWidthOfAutoColumn={undefined}
		>
			{selectable
				? shouldRenderNormalCheckbox && (
					<CenteredCheckbox
						value={selectable.isSelected}
						onChange={() => selectable.onSelect(ItemSelectionKind.Append)}
						shouldBubbleClick={false}
					/>
				)
				: !shouldHideDisabledCheckboxes && (
					<CenteredCheckbox
						value={false}
						isDisabled
					/>
				)
			}
		</InternalCell>
	);
};
SelectCell.displayName = "SelectCell";

type InternalCellProps = {
	isClickable: boolean;
	columnTrackingId: string | undefined;
	maxWidthOfAutoColumn: number | undefined;
	onClick?(event: MouseEvent): void;
	onDoubleClick?(event: MouseEvent): void;
};

const InternalCell = (props: PropsWithChildren<InternalCellProps>): ReactElement => {
	const {
		isClickable,
		columnTrackingId,
		maxWidthOfAutoColumn,
		onClick,
		onDoubleClick,
		children
	} = props;

	const {
		isDisabled
	} = useContext(RowContext);

	return (
		<div
			{...setTrackingId(
				`cell`,
				columnTrackingId ? `|${columnTrackingId}` : undefined
			)}
			className={classNames(
				styles.cell,
				{
					[styles.cell_clickable]: isClickable,
					[styles.cell_disabled]: isDisabled
				}
			)}
			onClick={onClick}
			onDoubleClick={onDoubleClick}
			style={{maxWidth: maxWidthOfAutoColumn}}
		>
			{children}
		</div>
	);
};
InternalCell.displayName = "InternalCell";

